import React from 'react';
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';

/**
 * ExportReportModal - Export report data to PDF or Excel
 */
export const ExportReportModal = ({ show, onHide, onExport }) => {
    const [exportFormat, setExportFormat] = React.useState('pdf');
    const [notifyByEmail, setNotifyByEmail] = React.useState(false);
    const [notifyEmail, setNotifyEmail] = React.useState('');
    const [selectedSections, setSelectedSections] = React.useState({
        projectOverview: true,
        tasksByStatus: true,
        taskDistribution: true,
        workflows: true,
        upcomingDeadlines: true,
        tasksByResource: true,
        teamPerformance: true,
        projectTimeline: true,
        recentTimeLogs: true
    });

    const handleSectionChange = (section) => {
        setSelectedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleSelectAll = () => {
        const allSelected = Object.values(selectedSections).every(val => val);
        const newState = {};
        Object.keys(selectedSections).forEach(key => {
            newState[key] = !allSelected;
        });
        setSelectedSections(newState);
    };

    const handleExport = () => {
        if (onExport) {
            onExport({
                format: exportFormat,
                sections: selectedSections,
                notifyByEmail: notifyByEmail,
                notifyEmail: notifyByEmail ? notifyEmail : null
            });
        }
        onHide();
    };

    const allSelected = Object.values(selectedSections).every(val => val);

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header>
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                        <Modal.Title className="display-1">Export Report</Modal.Title>
                        <div className='display-2 d-block'></div>
                    </div>
                    <button 
                        type="button" 
                        className="btn-close" 
                        aria-label="Close"
                        onClick={onHide}
                    ></button>
                </div>
            </Modal.Header>
            <Modal.Body>
                {/* Export Format Selection */}
                <div className="mb-4">
                    <label className="form-label fw-semibold mb-3">Choose Export Format</label>
                    <Row className="g-3">
                        <Col sm={6}>
                            <Button
                                variant={exportFormat === 'pdf' ? 'danger' : 'outline-danger'}
                                className="w-100 py-3"
                                onClick={() => setExportFormat('pdf')}
                            >
                                <i className="fas fa-file-pdf fa-2x mb-2 d-block"></i>
                                Export as PDF
                            </Button>
                        </Col>
                        <Col sm={6}>
                            <Button
                                variant={exportFormat === 'excel' ? 'success' : 'outline-success'}
                                className="w-100 py-3"
                                onClick={() => setExportFormat('excel')}
                            >
                                <i className="fas fa-file-excel fa-2x mb-2 d-block"></i>
                                Export as Excel
                            </Button>
                        </Col>
                    </Row>
                </div>

                {/* Select Report Sections */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <label className="form-label fw-semibold mb-0">Select Report Sections</label>
                        <Button 
                            variant="link" 
                            size="sm" 
                            className="text-decoration-none p-0"
                            onClick={handleSelectAll}
                        >
                            {allSelected ? 'Deselect All' : 'Select All'}
                        </Button>
                    </div>
                    <div className="border rounded p-3">
                        <Row className="g-2">
                            <Col sm={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="section-overview"
                                    label="Project Overview"
                                    checked={selectedSections.projectOverview}
                                    onChange={() => handleSectionChange('projectOverview')}
                                />
                            </Col>
                            <Col sm={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="section-tasks-status"
                                    label="Tasks by Status"
                                    checked={selectedSections.tasksByStatus}
                                    onChange={() => handleSectionChange('tasksByStatus')}
                                />
                            </Col>
                            <Col sm={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="section-distribution"
                                    label="Task Distribution"
                                    checked={selectedSections.taskDistribution}
                                    onChange={() => handleSectionChange('taskDistribution')}
                                />
                            </Col>
                            <Col sm={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="section-workflows"
                                    label="Workflows"
                                    checked={selectedSections.workflows}
                                    onChange={() => handleSectionChange('workflows')}
                                />
                            </Col>
                            <Col sm={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="section-deadlines"
                                    label="Upcoming Deadlines"
                                    checked={selectedSections.upcomingDeadlines}
                                    onChange={() => handleSectionChange('upcomingDeadlines')}
                                />
                            </Col>
                            <Col sm={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="section-resources"
                                    label="Tasks by Resource"
                                    checked={selectedSections.tasksByResource}
                                    onChange={() => handleSectionChange('tasksByResource')}
                                />
                            </Col>
                            <Col sm={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="section-performance"
                                    label="Team Performance"
                                    checked={selectedSections.teamPerformance}
                                    onChange={() => handleSectionChange('teamPerformance')}
                                />
                            </Col>
                            <Col sm={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="section-timeline"
                                    label="Project Timeline"
                                    checked={selectedSections.projectTimeline}
                                    onChange={() => handleSectionChange('projectTimeline')}
                                />
                            </Col>
                            <Col sm={6}>
                                <Form.Check
                                    type="checkbox"
                                    id="section-timelogs"
                                    label="Recent Time Logs"
                                    checked={selectedSections.recentTimeLogs}
                                    onChange={() => handleSectionChange('recentTimeLogs')}
                                />
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* Notify by Email */}
                <div className="border-top pt-3">
                    <Form.Check
                        type="checkbox"
                        id="notify-email"
                        label="Notify me by email when export is ready "
                        checked={notifyByEmail}
                        onChange={(e) => setNotifyByEmail(e.target.checked)}
                    />
                    
                    {/* Email Input Field (shows when checkbox is checked) */}
                    {notifyByEmail && (
                        <div className="mt-3 ms-4">
                            <Form.Group>
                                <Form.Label className="small text-muted">Enter Notification Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="example@domain.com"
                                    value={notifyEmail}
                                    onChange={(e) => setNotifyEmail(e.target.value)}
                                    required
                                />
                            </Form.Group>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleExport}
                    disabled={!Object.values(selectedSections).some(val => val)}
                >
                    <i className="fas fa-download me-2"></i>
                    Export Report
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * ViewTaskDetailsModal - Show task details in report
 */
export const ViewTaskDetailsModal = ({ show, onHide, task }) => {
    if (!task) return null;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header>
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                        <Modal.Title className="display-1">Task Details</Modal.Title>
                        <div className='display-2 d-block'></div>
                    </div>
                    <button 
                        type="button" 
                        className="btn-close" 
                        aria-label="Close"
                        onClick={onHide}
                    ></button>
                </div>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="text-muted small mb-1">Task Name</label>
                            <div className="fw-semibold">{task.name}</div>
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="text-muted small mb-1">Status</label>
                            <div>
                                <span className={`badge bg-${task.statusColor || 'secondary'}`}>
                                    {task.status}
                                </span>
                            </div>
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="text-muted small mb-1">Priority</label>
                            <div>
                                <span className={`badge bg-${task.priorityColor || 'secondary'}`}>
                                    {task.priority}
                                </span>
                            </div>
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="text-muted small mb-1">Due Date</label>
                            <div className="fw-semibold">{task.dueDate}</div>
                        </div>
                    </Col>
                    <Col md={12}>
                        <div className="mb-3">
                            <label className="text-muted small mb-1">Assigned To</label>
                            <div className="fw-semibold">{task.assignedTo || 'Unassigned'}</div>
                        </div>
                    </Col>
                    <Col md={12}>
                        <div className="mb-3">
                            <label className="text-muted small mb-1">Description</label>
                            <div className="text-muted">{task.description || 'No description available'}</div>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * ViewMemberDetailsModal - Show team member performance details
 */
export const ViewMemberDetailsModal = ({ show, onHide, member }) => {
    if (!member) return null;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>Team Member Performance</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">
                    <div className='d-flex align-items-center gap-3 p-3 bg-light rounded-3'>
                        {member.avatar ? (
                            <img 
                                src={member.avatar} 
                                alt={member.name}
                                className='avatar-60 rounded-circle'
                            />
                        ) : (
                            <div className='avatar-60 rounded-circle avatar-gradient d-flex align-items-center justify-content-center text-white fw-bold'>
                                {member.initials}
                            </div>
                        )}
                        <div>
                            <div className='fw-bold fs-5 mb-1'>{member.name}</div>
                            <div className='text-muted'>{member.role}</div>
                        </div>
                    </div>
                </div>

                <Row className="g-3 mb-4">
                    <Col sm={6}>
                        <div className="bg-white border rounded-3 p-3">
                            <div className="d-flex align-items-center gap-3">
                                <div 
                                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                    style={{ 
                                        width: '48px', 
                                        height: '48px',
                                        backgroundColor: '#3b82f620',
                                        color: '#3b82f6'
                                    }}
                                >
                                    <i className="fas fa-tasks"></i>
                                </div>
                                <div className="flex-grow-1">
                                    <div className="fw-bold fs-4 text-primary">{member.tasksAssigned || 0}</div>
                                    <div className="text-muted small">Tasks Assigned</div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col sm={6}>
                        <div className="bg-white border rounded-3 p-3">
                            <div className="d-flex align-items-center gap-3">
                                <div 
                                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                    style={{ 
                                        width: '48px', 
                                        height: '48px',
                                        backgroundColor: '#10b98120',
                                        color: '#10b981'
                                    }}
                                >
                                    <i className="fas fa-check-circle"></i>
                                </div>
                                <div className="flex-grow-1">
                                    <div className="fw-bold fs-4 text-success">{member.tasksCompleted || 0}</div>
                                    <div className="text-muted small">Tasks Completed</div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col sm={6}>
                        <div className="bg-white border rounded-3 p-3">
                            <div className="d-flex align-items-center gap-3">
                                <div 
                                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                    style={{ 
                                        width: '48px', 
                                        height: '48px',
                                        backgroundColor: '#f59e0b20',
                                        color: '#f59e0b'
                                    }}
                                >
                                    <i className="fas fa-clock"></i>
                                </div>
                                <div className="flex-grow-1">
                                    <div className="fw-bold fs-4 text-warning">{member.tasksPending || 0}</div>
                                    <div className="text-muted small">Pending Tasks</div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col sm={6}>
                        <div className="bg-white border rounded-3 p-3">
                            <div className="d-flex align-items-center gap-3">
                                <div 
                                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                    style={{ 
                                        width: '48px', 
                                        height: '48px',
                                        backgroundColor: '#06b6d420',
                                        color: '#06b6d4'
                                    }}
                                >
                                    <i className="fas fa-chart-line"></i>
                                </div>
                                <div className="flex-grow-1">
                                    <div className="fw-bold fs-4 text-info">{member.efficiency || 0}%</div>
                                    <div className="text-muted small">Efficiency Score</div>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                <div className="bg-light rounded-3 p-3">
                    <div className="small text-muted mb-2">Task Progress</div>
                    <div className="progress progress-h-12 mb-2">
                        <div 
                            className="progress-bar progress-gradient-green" 
                            style={{ width: `${Math.round((member.tasksCompleted / member.tasksAssigned) * 100)}%` }}
                        ></div>
                    </div>
                    <div className="d-flex justify-content-between small">
                        <span className="fw-semibold">{Math.round((member.tasksCompleted / member.tasksAssigned) * 100)}% Complete</span>
                        <span className="text-muted">{member.tasksCompleted} of {member.tasksAssigned} tasks</span>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Close
                </Button>
                <Button variant="primary">
                    View Full Profile
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// ============================================
// PROJECT REPORT FILTERS MODAL
// ============================================
// ============================================
// VIEW RESOURCE DETAILS MODAL
// ============================================
export const ViewResourceDetailsModal = ({ show, onHide, resource }) => {
    if (!resource) return null;

    // Sample task list data
    const taskList = [
        { taskName: 'Design Homepage UI', project: 'Website Redesign', status: 'In Progress', priority: 'High', startDate: '2024-01-05', endDate: '2024-01-15', estimatedHours: 24, loggedHours: 18 },
        { taskName: 'API Integration', project: 'Mobile App', status: 'Completed', priority: 'Medium', startDate: '2024-01-01', endDate: '2024-01-10', estimatedHours: 32, loggedHours: 30 },
        { taskName: 'Database Optimization', project: 'Backend System', status: 'Delayed', priority: 'High', startDate: '2023-12-20', endDate: '2024-01-05', estimatedHours: 16, loggedHours: 20 },
        { taskName: 'User Testing', project: 'Website Redesign', status: 'In Progress', priority: 'Low', startDate: '2024-01-08', endDate: '2024-01-12', estimatedHours: 12, loggedHours: 8 }
    ];

    // Sample time logged data
    const timeLoggedData = [
        { date: '2024-01-08', task: 'Design Homepage UI', hours: 6, description: 'Created wireframes and mockups for homepage sections' },
        { date: '2024-01-07', task: 'User Testing', hours: 4, description: 'Conducted usability testing with 5 users' },
        { date: '2024-01-06', task: 'API Integration', hours: 8, description: 'Completed REST API endpoints integration' },
        { date: '2024-01-05', task: 'Database Optimization', hours: 5, description: 'Query optimization and indexing' }
    ];

    const getStatusBadge = (status) => {
        const variants = {
            'Completed': 'success',
            'In Progress': 'primary',
            'Delayed': 'danger',
            'Pending': 'warning'
        };
        return variants[status] || 'secondary';
    };

    const getPriorityBadge = (priority) => {
        const variants = {
            'High': 'danger',
            'Medium': 'warning',
            'Low': 'info'
        };
        return variants[priority] || 'secondary';
    };
    
    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header>
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                        <Modal.Title className="display-1">Resource Details</Modal.Title>
                        <div className='display-2 d-block'></div>
                    </div>
                    <button 
                        type="button" 
                        className="btn-close" 
                        aria-label="Close"
                        onClick={onHide}
                    ></button>
                </div>
            </Modal.Header>
            <Modal.Body className="max-h-70vh">
                    {/* Resource Basic Info */}
                    <div className="border rounded-3 p-3 mb-4 bg-light">
                        <div className="row align-items-center">
                            <div className="col-md-8">
                                <div className="d-flex align-items-center">
                                    {resource.avatar ? (
                                        <img 
                                            src={resource.avatar} 
                                            alt={resource.name} 
                                            className="avatar-60 rounded-circle me-3"
                                        />
                                    ) : (
                                        <div className="avatar-60 rounded-circle avatar-gradient d-inline-flex align-items-center justify-content-center me-3 flex-shrink-0">
                                            {resource.initials}
                                        </div>
                                    )}
                                    <div>
                                        <h5 className="fw-bold mb-1">{resource.name}</h5>
                                        <div className="text-muted small mb-1">
                                            {resource.role}
                                        </div>
                                        <div className="text-muted small">
                                            {resource.department || 'Engineering Department'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4 text-md-end mt-3 mt-md-0">
                                <div className="d-inline-flex align-items-center gap-2">
                                    <span className="badge bg-success-subtle text-success px-3 py-2">
                                        <i className="fas fa-circle me-1"></i>
                                        Active
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                {/* Summary Cards */}
                <div className="mb-4">
                    <h6 className="fw-semibold mb-3">Task Summary</h6>
                    <div className="row g-3">
                        <div className="col-md-3 col-6">
                            <div 
                                className="border rounded-3 h-100 p-3 status-card-hover"
                                style={{ 
                                    backgroundColor: '#3b82f608',
                                    borderColor: '#3b82f630'
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <div 
                                        className="status-icon-box d-flex align-items-center justify-content-center"
                                        style={{ 
                                            backgroundColor: '#3b82f620',
                                            color: '#3b82f6'
                                        }}
                                    >
                                        <i className="fas fa-tasks"></i>
                                    </div>
                                    <div className="fw-bold fs-28" style={{ color: '#3b82f6' }}>
                                        {resource.tasksAssigned || resource.assignedTasks || 0}
                                    </div>
                                </div>
                                <div className="fw-semibold text-dark mb-1">Total Tasks</div>
                            </div>
                        </div>
                        <div className="col-md-3 col-6">
                            <div 
                                className="border rounded-3 h-100 p-3 status-card-hover"
                                style={{ 
                                    backgroundColor: '#10b98108',
                                    borderColor: '#10b98130'
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <div 
                                        className="status-icon-box d-flex align-items-center justify-content-center"
                                        style={{ 
                                            backgroundColor: '#10b98120',
                                            color: '#10b981'
                                        }}
                                    >
                                        <i className="fas fa-check-circle"></i>
                                    </div>
                                    <div className="fw-bold fs-28" style={{ color: '#10b981' }}>
                                        {resource.tasksCompleted || resource.completedTasks || 0}
                                    </div>
                                </div>
                                <div className="fw-semibold text-dark mb-1">Completed</div>
                            </div>
                        </div>
                        <div className="col-md-3 col-6">
                            <div 
                                className="border rounded-3 h-100 p-3 status-card-hover"
                                style={{ 
                                    backgroundColor: '#06b6d408',
                                    borderColor: '#06b6d430'
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <div 
                                        className="status-icon-box d-flex align-items-center justify-content-center"
                                        style={{ 
                                            backgroundColor: '#06b6d420',
                                            color: '#06b6d4'
                                        }}
                                    >
                                        <i className="fas fa-spinner"></i>
                                    </div>
                                    <div className="fw-bold fs-28" style={{ color: '#06b6d4' }}>
                                        {resource.tasksPending || resource.inProgressTasks || 0}
                                    </div>
                                </div>
                                <div className="fw-semibold text-dark mb-1">In Progress</div>
                            </div>
                        </div>
                        <div className="col-md-3 col-6">
                            <div 
                                className="border rounded-3 h-100 p-3 status-card-hover"
                                style={{ 
                                    backgroundColor: '#ef444408',
                                    borderColor: '#ef444430'
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <div 
                                        className="status-icon-box d-flex align-items-center justify-content-center"
                                        style={{ 
                                            backgroundColor: '#ef444420',
                                            color: '#ef4444'
                                        }}
                                    >
                                        <i className="fas fa-exclamation-triangle"></i>
                                    </div>
                                    <div className="fw-bold fs-28" style={{ color: '#ef4444' }}>
                                        {resource.delayedTasks || 2}
                                    </div>
                                </div>
                                <div className="fw-semibold text-dark mb-1">Delayed</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Task List Table */}
                <div className='c-card-1 mb-4'>
                    <div className='c-card-header-1 d-flex justify-content-between align-items-center'>
                        <span>Assigned Tasks</span>
                        <Link 
                            to="/projects/detail/tasks" 
                            className="text-primary text-decoration-none d-inline-flex align-items-center gap-2 view-all-link-hover text-md"
                        >
                            View All Tasks
                        </Link>
                    </div>
                    <div className='c-card-body-1 p-0'>
                        <Table responsive className='c-table'>
                            <thead>
                                <tr>
                                    <th>Task Name</th>
                                    <th>Project</th>
                                    <th width="100px" className='text-center'>Status</th>
                                    <th width="100px" className='text-center'>Priority</th>
                                    <th width="100px" className='text-center'>Start Date</th>
                                    <th width="100px" className='text-center'>End Date</th>
                                    <th width="100px" className='text-center'>Est. Hours</th>
                                    <th width="100px" className='text-center'>Logged Hours</th>
                                </tr>
                            </thead>
                            <tbody>
                                {taskList.map((task, index) => (
                                    <tr key={index}>
                                        <td className="fw-medium">{task.taskName}</td>
                                        <td>{task.project}</td>
                                        <td className='text-center'>
                                            <span className={`fw-semibold ${
                                                task.status === 'Completed' ? 'text-success' :
                                                task.status === 'In Progress' ? 'text-primary' :
                                                task.status === 'Delayed' ? 'text-danger' :
                                                'text-warning'
                                            }`}>
                                                {task.status}
                                            </span>
                                        </td>
                                        <td className='text-center'>
                                            <span className={`fw-semibold ${
                                                task.priority === 'High' ? 'text-danger' :
                                                task.priority === 'Medium' ? 'text-warning' :
                                                'text-info'
                                            }`}>
                                                {task.priority}
                                            </span>
                                        </td>
                                        <td className="text-center text-muted small">{task.startDate}</td>
                                        <td className="text-center text-muted small">{task.endDate}</td>
                                        <td className="text-center">{task.estimatedHours}h</td>
                                        <td className="text-center fw-semibold">{task.loggedHours}h</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>

                {/* Time Logged Summary */}
                <div className='c-card-1 mb-4'>
                    <div className='c-card-header-1 d-flex justify-content-between align-items-center'>
                        <span>Recent Time Logs</span>
                        <Link 
                            to="/projects/detail/logs" 
                            className="text-primary text-decoration-none d-inline-flex align-items-center gap-2 view-all-link-hover text-md"
                        >
                            View All Logs
                        </Link>
                    </div>
                    <div className='c-card-body-1 p-0'>
                        <Table responsive className='c-table'>
                            <thead>
                                <tr>
                                    <th width="100px" className='text-center'>Date</th>
                                    <th>Task</th>
                                    <th width="120px" className='text-center'>Hours Logged</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timeLoggedData.map((log, index) => (
                                    <tr key={index}>
                                        <td className="text-center text-muted small">{log.date}</td>
                                        <td className="fw-medium">{log.task}</td>
                                        <td className="text-center fw-semibold text-info">{log.hours}h</td>
                                        <td className="text-muted small">{log.description}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>

                {/* Notes Section */}
                <div className="row g-3">
                    <div className="col-md-6">
                        <h6 className="fw-semibold mb-2">PM Notes</h6>
                        <div className="border rounded-3 p-3 bg-light">
                            <p className="text-muted small mb-0">
                                Excellent performance on recent projects. Shows strong technical skills and good communication with team members. Recommended for lead role on upcoming project.
                            </p>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <h6 className="fw-semibold mb-2">Resource Notes</h6>
                        <div className="border rounded-3 p-3 bg-light">
                            <p className="text-muted small mb-0">
                                Currently working on multiple high-priority tasks. May need support on database optimization task due to complexity. Available for code reviews during afternoon hours.
                            </p>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Close
                </Button>
                <Button variant="primary">
                    View Full Profile
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// ============================================
// VIEW TIME LOG DETAILS MODAL
// ============================================
export const ViewTimeLogDetailsModal = ({ show, onHide, timeLog }) => {
    if (!timeLog) return null;

    // Sample detailed timesheet data
    const timesheetEntries = [
        { date: '2024-01-08', taskName: 'Design Homepage UI', projectName: 'Website Redesign', hours: 6, billable: true, description: 'Created wireframes and mockups for homepage sections' },
        { date: '2024-01-08', taskName: 'User Testing', projectName: 'Website Redesign', hours: 2, billable: true, description: 'Conducted usability testing with users' },
        { date: '2024-01-07', taskName: 'API Integration', projectName: 'Mobile App', hours: 8, billable: true, description: 'REST API endpoints integration' },
        { date: '2024-01-06', taskName: 'Database Optimization', projectName: 'Backend System', hours: 5, billable: false, description: 'Query optimization and indexing' },
        { date: '2024-01-06', taskName: 'Code Review', projectName: 'Backend System', hours: 3, billable: false, description: 'Reviewed pull requests and provided feedback' }
    ];

    // Sample activity breakdown
    const hoursByTask = [
        { task: 'Design Homepage UI', hours: 12 },
        { task: 'API Integration', hours: 16 },
        { task: 'User Testing', hours: 8 },
        { task: 'Database Optimization', hours: 10 }
    ];

    const hoursByProject = [
        { project: 'Website Redesign', hours: 20 },
        { project: 'Mobile App', hours: 16 },
        { project: 'Backend System', hours: 18 }
    ];

    const totalBillableHours = timesheetEntries.filter(e => e.billable).reduce((sum, e) => sum + e.hours, 0);
    const totalNonBillableHours = timesheetEntries.filter(e => !e.billable).reduce((sum, e) => sum + e.hours, 0);

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header>
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                        <Modal.Title className="display-1">Time Log Details</Modal.Title>
                        <div className='display-2 d-block'></div>
                    </div>
                    <button 
                        type="button" 
                        className="btn-close" 
                        aria-label="Close"
                        onClick={onHide}
                    ></button>
                </div>
            </Modal.Header>
            <Modal.Body className="max-h-70vh">
                    {/* Resource Info */}
                    <div className="border rounded-3 p-3 mb-4 bg-light">
                        <div className="row align-items-center">
                            <div className="col-md-8">
                                <div className="d-flex align-items-center">
                                    <div className="avatar-60 rounded-circle avatar-gradient d-inline-flex align-items-center justify-content-center me-3 flex-shrink-0">
                                        {timeLog.resourceName ? timeLog.resourceName.split(' ').map(n => n[0]).join('') : 'NA'}
                                    </div>
                                    <div>
                                        <h5 className="fw-bold mb-1">{timeLog.resourceName}</h5>
                                        <div className="text-muted small mb-1">
                                            Senior Developer
                                        </div>
                                        <div className="text-muted small">
                                            Engineering Department
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4 text-md-end mt-3 mt-md-0">
                                <div className="d-inline-flex align-items-center gap-2">
                                    <span className="badge bg-success-subtle text-success px-3 py-2">
                                        <i className="fas fa-circle me-1"></i>
                                        Active
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Time Log Summary */}
                    <div className="mb-4">
                        <h6 className="fw-semibold mb-3">Time Log Summary</h6>
                        <div className="row g-3">
                            <div className="col-md-3 col-6">
                                <div 
                                    className="border rounded-3 h-100 p-3 status-card-hover"
                                    style={{ 
                                        backgroundColor: '#3b82f608',
                                        borderColor: '#3b82f630'
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div 
                                            className="status-icon-box d-flex align-items-center justify-content-center"
                                            style={{ 
                                                backgroundColor: '#3b82f620',
                                                color: '#3b82f6'
                                            }}
                                        >
                                            <i className="fas fa-clock"></i>
                                        </div>
                                        <div className="fw-bold fs-28" style={{ color: '#3b82f6' }}>
                                            {totalBillableHours + totalNonBillableHours}h
                                        </div>
                                    </div>
                                    <div className="fw-semibold text-dark mb-1">Total Hours</div>
                                </div>
                            </div>
                            <div className="col-md-3 col-6">
                                <div 
                                    className="border rounded-3 h-100 p-3 status-card-hover"
                                    style={{ 
                                        backgroundColor: '#10b98108',
                                        borderColor: '#10b98130'
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div 
                                            className="status-icon-box d-flex align-items-center justify-content-center"
                                            style={{ 
                                                backgroundColor: '#10b98120',
                                                color: '#10b981'
                                            }}
                                        >
                                            <i className="fas fa-dollar-sign"></i>
                                        </div>
                                        <div className="fw-bold fs-28" style={{ color: '#10b981' }}>
                                            {totalBillableHours}h
                                        </div>
                                    </div>
                                    <div className="fw-semibold text-dark mb-1">Billable Hours</div>
                                </div>
                            </div>
                            <div className="col-md-3 col-6">
                                <div 
                                    className="border rounded-3 h-100 p-3 status-card-hover"
                                    style={{ 
                                        backgroundColor: '#6b728008',
                                        borderColor: '#6b728030'
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div 
                                            className="status-icon-box d-flex align-items-center justify-content-center"
                                            style={{ 
                                                backgroundColor: '#6b728020',
                                                color: '#6b7280'
                                            }}
                                        >
                                            <i className="fas fa-ban"></i>
                                        </div>
                                        <div className="fw-bold fs-28" style={{ color: '#6b7280' }}>
                                            {totalNonBillableHours}h
                                        </div>
                                    </div>
                                    <div className="fw-semibold text-dark mb-1">Non-Billable</div>
                                </div>
                            </div>
                            <div className="col-md-3 col-6">
                                <div 
                                    className="border rounded-3 h-100 p-3 status-card-hover"
                                    style={{ 
                                        backgroundColor: '#8b5cf608',
                                        borderColor: '#8b5cf630'
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div 
                                            className="status-icon-box d-flex align-items-center justify-content-center"
                                            style={{ 
                                                backgroundColor: '#8b5cf620',
                                                color: '#8b5cf6'
                                            }}
                                        >
                                            <i className="fas fa-file-alt"></i>
                                        </div>
                                        <div className="fw-bold fs-28" style={{ color: '#8b5cf6' }}>
                                            {timesheetEntries.length}
                                        </div>
                                    </div>
                                    <div className="fw-semibold text-dark mb-1">Entries</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Timesheet Table */}
                    <div className='c-card-1 mb-4'>
                        <div className='c-card-header-1'>
                            <span>Detailed Timesheet</span>
                        </div>
                        <div className='c-card-body-1 p-0'>
                            <Table responsive className='c-table'>
                                <thead>
                                    <tr>
                                        <th width="100px" className='text-center'>Date</th>
                                        <th>Task Name</th>
                                        <th>Project Name</th>
                                        <th width="100px" className='text-center'>Hours</th>
                                        <th width="120px" className='text-center'>Billable</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {timesheetEntries.map((entry, index) => (
                                        <tr key={index}>
                                            <td className="text-center text-muted small">{entry.date}</td>
                                            <td className="fw-medium">{entry.taskName}</td>
                                            <td>{entry.projectName}</td>
                                            <td className="text-center fw-semibold text-info">{entry.hours}h</td>
                                            <td className='text-center'>
                                                <span className={`fw-semibold ${entry.billable ? 'text-success' : 'text-secondary'}`}>
                                                    {entry.billable ? 'Billable' : 'Non-Billable'}
                                                </span>
                                            </td>
                                            <td className="text-muted small">{entry.description}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>

                    {/* Activity Breakdown */}
                    <div className="mb-4">
                        <h6 className="fw-semibold mb-3">Activity Breakdown</h6>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <div className="border rounded-3 p-3">
                                    <h6 className="fw-semibold mb-3 small">Hours by Task</h6>
                                    {hoursByTask.map((item, index) => (
                                        <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="small text-muted">{item.task}</span>
                                            <span className="fw-semibold text-primary">{item.hours}h</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="border rounded-3 p-3">
                                    <h6 className="fw-semibold mb-3 small">Hours by Project</h6>
                                    {hoursByProject.map((item, index) => (
                                        <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="small text-muted">{item.project}</span>
                                            <span className="fw-semibold text-success">{item.hours}h</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Notes Section */}
                    <div className="row g-3">
                        <div className="col-md-6">
                            <h6 className="fw-semibold mb-2">PM Notes</h6>
                            <div className="border rounded-3 p-3 bg-light">
                                <p className="text-muted small mb-0">
                                    Consistent time logging and excellent adherence to billable hours targets. Time entries are detailed and well-documented.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <h6 className="fw-semibold mb-2">Resource Notes</h6>
                            <div className="border rounded-3 p-3 bg-light">
                                <p className="text-muted small mb-0">
                                    Working on multiple projects simultaneously. Ensuring proper time tracking for accurate billing and project management.
                                </p>
                            </div>
                        </div>
                    </div>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Close
                </Button>
                <Button variant="primary">
                    <i className="fas fa-edit me-2"></i>
                    Edit Timesheet
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
